



















































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import SetupMasterDataStepComponent from "@/components/admin/clients/setup/SetupMasterDataStep.vue";
import SetupProductsStepComponent from "@/components/admin/clients/setup/SetupProductsStep.vue";
import SetupProjectTypesStepComponent from "@/components/admin/clients/setup/SetupProjectTypesStep.vue";
import SetupProjectOrganisationStepComponent from "@/components/admin/clients/setup/SetupProjectOrganisationStep.vue";
import SetupFunctionsStepComponent from "@/components/admin/clients/setup/SetupFunctionsStep.vue";
import SetupLicencesStepComponent from "@/components/admin/clients/setup/SetupLicencesStep.vue";
import SetupContractsStepComponent from "@/components/admin/clients/setup/SetupContractsStep.vue";
import SetupUsersStepComponent from "@/components/admin/clients/setup/SetupUsersStep.vue";
import SetupSummaryStepComponent from "@/components/admin/clients/setup/SetupSummaryStep.vue";
import SetupExternalSystemsStepComponent from "@/components/admin/clients/setup/SetupExternalSystemsStep.vue";
import ClientPartnerPasswordDialogComponent from "@/components/admin/clients/partners/ClientPartnerPasswordDialog.vue";
import { ClientProductsValue } from "@/data/models/ClientAdministrationRow";
import { ClientSetupMaster, ClientSetupOptions } from "@/data/models/ClientSetup";
import { createClient, getClientSetupOptions } from "@/data/repository/ClientAdministrationRepository";
import { doesOtherClientWithEmailExist, doesOtherClientWithIresProjectNumberPrefixExist } from "@/data/repository/ClientRepository";
import router from "@/router/router";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { clientPageTitle } from "@/utils/ClientAdministrationDetail";
import { requestClientListReload } from "@/utils/ClientListRowUpdates";
import { masterDataValue } from "@/utils/ClientMasterData";
import {
  ClientSetupDraft,
  SetupStep,
  applyProductsChange,
  clientSetupErrorKey,
  createClientSetupDraft,
  hasSetupInput,
  isStepComplete,
  setupSteps,
  toClientSetupRequest,
} from "@/utils/ClientSetupDraft";
import { setupSummary } from "@/utils/ClientSetupSummary";
import { ComponentName } from "@/utils/RouteGuards";
import { formatNumber } from "@/utils/Utils";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "content-state": ContentStateComponent,
    "setup-master-data-step": SetupMasterDataStepComponent,
    "setup-products-step": SetupProductsStepComponent,
    "setup-project-types-step": SetupProjectTypesStepComponent,
    "setup-project-organisation-step": SetupProjectOrganisationStepComponent,
    "setup-functions-step": SetupFunctionsStepComponent,
    "setup-licences-step": SetupLicencesStepComponent,
    "setup-contracts-step": SetupContractsStepComponent,
    "setup-users-step": SetupUsersStepComponent,
    "setup-summary-step": SetupSummaryStepComponent,
    "setup-external-systems-step": SetupExternalSystemsStepComponent,
    "client-partner-password-dialog": ClientPartnerPasswordDialogComponent,
  },
})
export default class ClientSetup extends Vue {
  @Ref("confirm") readonly confirmDialog!: ConfirmationDialogComponent;
  @Ref("passwordDialog") readonly passwordDialog!: ClientPartnerPasswordDialogComponent;

  /** The one draft every step edits; nothing is stored before ANLEGEN. */
  draft: ClientSetupDraft = createClientSetupDraft();
  options: ClientSetupOptions | null = null;
  /** A member's master with the settings it follows; null otherwise or while loading. */
  master: ClientSetupMaster | null = null;
  currentStep = 1;
  isLoading = true;
  loadError = false;
  busy = false;
  checkingStep: SetupStep | null = null;
  /** Set when leaving is already decided (after ANLEGEN, or confirmed), so the guard does not ask again. */
  allowNavigation = false;

  /** IRES Cloud's or the step check's findings, shown at their fields until the field changes. */
  prefixTaken = false;
  emailTaken = false;
  nicknameTaken = false;
  staffNumberTaken = false;
  userEmailTaken = false;
  externalSystemEmailTaken = false;

  /** The steps shown: Externe Systeme only with API (#2610084, merge 4). */
  get steps(): SetupStep[] {
    return setupSteps(this.draft);
  }

  get masters(): ClientSetupMaster[] {
    return this.options?.masters ?? [];
  }

  @Watch("draft.masterData.iresProjectNumberPrefix")
  onPrefixChanged() {
    this.prefixTaken = false;
  }

  @Watch("draft.email")
  onEmailChanged() {
    this.emailTaken = false;
  }

  @Watch("draft.externalSystem", { deep: true })
  onExternalSystemChanged() {
    this.externalSystemEmailTaken = false;
  }

  @Watch("draft.firstUser", { deep: true })
  onFirstUserChanged() {
    this.nicknameTaken = false;
    this.staffNumberTaken = false;
    this.userEmailTaken = false;
  }

  /** A member's steps show its master's settings, loaded when the master is chosen. */
  @Watch("draft.masterClientId")
  @Watch("draft.groupKind")
  async onMasterChanged() {
    const masterClientId = this.draft.groupKind === "MEMBER" ? this.draft.masterClientId : null;
    this.master = null;
    // Another master has other systems: links chosen for the previous one do not carry over.
    this.draft.linkedExternalSystemIds = [];
    if (masterClientId === null) {
      return;
    }
    try {
      const loaded = await getClientSetupOptions(masterClientId);
      // A later choice wins over a slower earlier answer.
      if (this.draft.masterClientId === masterClientId && this.draft.groupKind === "MEMBER") {
        this.master = loaded.masters.find((master) => master.id === masterClientId) ?? null;
      }
    } catch {
      // The member steps then say "as the MASTER-Mandant" without the values; ANLEGEN is unaffected.
    }
  }

  isComplete(step: SetupStep): boolean {
    return isStepComplete(step, this.draft, this.options);
  }

  /** A completed step's one-line caption: the Mandant by name and Kürzel, the others as the summary says. */
  captionOf(step: SetupStep): string {
    if (step === "masterData") {
      const name = masterDataValue("clientName", this.draft.masterData.clientName) ?? "";
      const prefix = masterDataValue("iresProjectNumberPrefix", this.draft.masterData.iresProjectNumberPrefix) ?? "";
      return prefix ? `${name} (${prefix})` : name;
    }
    const section = setupSummary(this.draft, this.options, this.master, (key, values) => this.$t(key, values as never).toString(), formatNumber)
      .find((entry) => entry.step === step);
    return section ? section.lines.map((line) => line.value).join(" · ") : "";
  }

  onProductsChanged(before: ClientProductsValue) {
    applyProductsChange(this.draft, this.options, before);
  }

  goToStep(step: SetupStep) {
    this.currentStep = this.steps.indexOf(step) + 1;
  }

  /** WEITER; the Mandant step first asks whether the Kürzel and the address are free, so a clash shows here. */
  async onNext(step: SetupStep, index: number) {
    if (step === "masterData") {
      this.busy = true;
      this.checkingStep = step;
      const prefix = masterDataValue("iresProjectNumberPrefix", this.draft.masterData.iresProjectNumberPrefix) ?? "";
      const email = this.draft.email.trim().toLowerCase();
      const [prefixTaken, emailTaken] = await Promise.all([
        doesOtherClientWithIresProjectNumberPrefixExist("", prefix),
        doesOtherClientWithEmailExist("", email),
      ]);
      this.busy = false;
      this.checkingStep = null;
      this.prefixTaken = prefixTaken;
      this.emailTaken = emailTaken;
      if (prefixTaken || emailTaken) {
        return;
      }
    }
    this.currentStep = index + 2;
  }

  async onCreate() {
    this.busy = true;
    try {
      const request = toClientSetupRequest(this.draft);
      const result = await createClient(request);
      SnackbarModule.showSuccess({ key: "admin.clients.setup.created" });
      requestClientListReload();
      this.allowNavigation = true;
      // The external system's password, shown this once (#2610084, merge 4), before the detail opens.
      if (result.externalSystem !== null && request.externalSystem !== null) {
        await this.passwordDialog.show(request.externalSystem.name, request.externalSystem.email, result.externalSystem.password);
      }
      router.replace({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: result.clientId } });
    } catch (error) {
      this.busy = false;
      const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
      this.showRefusalAtItsStep(reason);
      SnackbarModule.showError({ key: clientSetupErrorKey(reason) });
    }
  }

  /** A refusal that belongs to a field takes the user back to that field's step. */
  showRefusalAtItsStep(reason: unknown) {
    switch (reason) {
      case "PREFIX_TAKEN":
        this.prefixTaken = true;
        this.goToStep("masterData");
        break;
      case "EMAIL_TAKEN":
        this.emailTaken = true;
        this.goToStep("masterData");
        break;
      case "NICKNAME_TAKEN":
        this.nicknameTaken = true;
        this.goToStep("users");
        break;
      case "STAFF_NUMBER_TAKEN":
        this.staffNumberTaken = true;
        this.goToStep("users");
        break;
      case "USER_EMAIL_TAKEN":
        this.userEmailTaken = true;
        this.goToStep("users");
        break;
      case "EXTERNAL_SYSTEM_EMAIL_TAKEN":
        this.externalSystemEmailTaken = true;
        this.goToStep("externalSystems");
        break;
      case "NOT_THE_MASTERS_SYSTEM":
        this.goToStep("externalSystems");
        break;
    }
  }

  onBack() {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_LIST });
  }

  /** Leaving with something entered asks first, like an edit dialog's ABBRECHEN (edit-mode-dialogs.md). */
  async beforeRouteLeave(_to: Route, _from: Route, next: (allow?: boolean) => void) {
    if (this.allowNavigation || !hasSetupInput(this.draft)) {
      next();
      return;
    }
    const leave = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (leave) {
      this.allowNavigation = true;
    }
    next(leave === true);
  }

  async mounted() {
    ToolbarContentModule.setToolbarTitle(
      clientPageTitle(this.$t("admin.clients.title").toString(), null, this.$t("admin.clients.setup.title").toString())
    );
    try {
      this.options = await getClientSetupOptions(null);
    } catch {
      // Shown as the page's failure state; the back button is the way out.
      this.loadError = true;
    }
    this.isLoading = false;
  }
}
